import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { authClient } from '@/lib/auth-client';
import { useAuthConfig } from '@/lib/auth-config';
import { type AuthPageSearch, socialCallbackUrl, socialErrorUrl } from '@/lib/oauth';

/** Google "G" mark — lucide ships no brand icons, so this is inline. */
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.806.54-1.8368.8591-3.0477.8591-2.3441 0-4.3282-1.5831-5.0359-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.9641 10.71c-.18-.54-.2823-1.1168-.2823-1.71s.1023-1.17.2823-1.71V4.9582H.9573C.3477 6.1732 0 7.5477 0 9s.3477 2.8268.9573 4.0418L3.9641 10.71z"
      />
      <path
        fill="#EA4335"
        d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.4259 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.9641 7.29C4.6718 5.1627 6.6559 3.5795 9 3.5795z"
      />
    </svg>
  );
}

export interface SocialButtonsProps {
  /** The page's current search params — decides the post-redirect landing target. */
  search: AuthPageSearch;
  /** Surface a start-of-flow failure via the page's existing Alert. */
  onError: (message: string) => void;
}

/**
 * Social sign-in buttons for login/signup (plan P3). Renders NOTHING when the
 * server lists no providers — no stray divider above the email form. Clicking
 * starts better-auth's social flow, which navigates the browser away; an error
 * return means the flow never started and is reported via onError. Providers
 * are additive: a new one adds a flag + a button (plan AC11).
 */
export function SocialButtons({ search, onError }: SocialButtonsProps) {
  const { socialProviders } = useAuthConfig();
  const [loading, setLoading] = useState(false);

  const showGoogle = socialProviders.includes('google');
  if (!showGoogle) return null;

  async function handleGoogle() {
    setLoading(true);
    try {
      const { error } = await authClient.signIn.social({
        provider: 'google',
        // Resume target for an in-flight MCP grant; error returns to this page
        // with its query intact (plan D5/AC7).
        callbackURL: socialCallbackUrl(search),
        errorCallbackURL: socialErrorUrl(),
      });
      if (error) {
        onError(error.message ?? 'Sign-in failed. Please try again.');
        setLoading(false);
      }
      // Success never reaches here — the browser is redirected to the provider.
    } catch {
      onError('Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="mb-6 space-y-4">
      <Button
        type="button"
        variant="outline"
        size="cta"
        className="w-full"
        disabled={loading}
        onClick={() => {
          void handleGoogle();
        }}
      >
        <GoogleMark />
        {loading ? 'Redirecting to Google…' : 'Continue with Google'}
      </Button>
      <div className="flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-xs text-faint">or continue with email</span>
        <Separator className="flex-1" />
      </div>
    </div>
  );
}
